import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';

import {
  AddSplitTab,
  BackLink,
  Card,
  Container,
  DeleteSplitButton,
  Discard,
  EditorGrid,
  EmptySplit,
  ExerciseInfo,
  ExerciseItem,
  ExerciseList,
  ExerciseOrder,
  Field,
  FieldLabel,
  FieldsRow,
  Header,
  HeaderActions,
  LibraryCard,
  LibraryCardInfo,
  LibraryCardsGrid,
  LibraryCardToggle,
  LibraryColumn,
  LibraryFiltersRow,
  LibraryGroupSelect,
  LibraryScroll,
  LibrarySearch,
  PageTitle,
  RemoveExerciseButton,
  Save,
  SectionHead,
  SmallLabel,
  SplitEditorColumn,
  SplitExercisesScroll,
  SplitHead,
  SplitHeadLabel,
  SplitHeadTitle,
  SplitTab,
  SplitTabGroup,
  SplitTabsRow,
  TextField,
} from './styles';

import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { TrainingTemplatesService } from '../../../../services/TrainingTemplatesService';
import { ExercisesService } from '../../../../services/ExercisesService';
import { IExercise } from '../../../../domains/Exercise';

const SPLIT_LETTERS = 'ABCDEFGH';

interface IDraftExercise {
  exercise_id: string;
  name: string;
  group: string;
}

interface IDraftSplit {
  exercises: IDraftExercise[];
}

function mainGroupName(exercise: IExercise): string {
  const main =
    exercise.muscle_groups.find(mg => mg.is_main) || exercise.muscle_groups[0];

  return main?.muscle_group?.name || main?.name || 'Outro';
}

function sortExercisesByGroup(
  exercises: IDraftExercise[],
): IDraftExercise[] {
  return [...exercises].sort(
    (a, b) => a.group.localeCompare(b.group) || a.name.localeCompare(b.name),
  );
}

function splitMuscleGroupsLabel(split: IDraftSplit): string {
  if (!split.exercises.length) {
    return 'Nenhum exercício ainda';
  }

  const groups = new Set(split.exercises.map(exercise => exercise.group));

  return Array.from(groups).join(', ');
}

const emptyDraftSplit = (): IDraftSplit => ({
  exercises: [],
});

const TrainingTemplateEditor: React.FC = () => {
  const trainingTemplatesService = new TrainingTemplatesService();
  const exercisesService = new ExercisesService();

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();

  const isEditing = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [focus, setFocus] = useState('');
  const [splits, setSplits] = useState<IDraftSplit[]>([emptyDraftSplit()]);
  const [activeSplitIndex, setActiveSplitIndex] = useState(0);

  const [libraryExercises, setLibraryExercises] = useState<IExercise[]>([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryGroupFilter, setLibraryGroupFilter] = useState('');

  useEffect(() => {
    exercisesService
      .getExercisesOfGym({ limit: 1000 })
      .then(response => setLibraryExercises(response.data.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    trainingTemplatesService
      .getById(id)
      .then(response => {
        const template = response.data;

        setName(template.name);
        setFocus(template.focus);
        setSplits(
          template.splits.map(split => ({
            exercises: sortExercisesByGroup(
              split.exercises.map(splitExercise => ({
                exercise_id: splitExercise.exercise_id,
                name: splitExercise.exercise.name,
                group: mainGroupName(splitExercise.exercise),
              })),
            ),
          })),
        );
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Não foi possível carregar esse template.',
        });
        navigate('/trainings/templates');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const activeSplit = splits[Math.min(activeSplitIndex, splits.length - 1)];

  const updateActiveSplit = useCallback(
    (patch: Partial<IDraftSplit>) => {
      setSplits(current =>
        current.map((split, index) =>
          index === activeSplitIndex ? { ...split, ...patch } : split,
        ),
      );
    },
    [activeSplitIndex],
  );

  const handleAddSplit = useCallback(() => {
    setSplits(current => [...current, emptyDraftSplit()]);
    setActiveSplitIndex(splits.length);
  }, [splits.length]);

  const handleRemoveSplit = useCallback(() => {
    if (splits.length <= 1) return;

    const lastIndex = splits.length - 1;

    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Apagar o Split ${SPLIT_LETTERS[lastIndex] || lastIndex + 1
        }? Todos os exercícios desse split serão perdidos.`,
      )
    ) {
      return;
    }

    setSplits(current => current.slice(0, -1));
    setActiveSplitIndex(current => Math.min(current, lastIndex - 1));
  }, [splits.length]);

  const toggleExercise = useCallback(
    (exercise: IExercise) => {
      const alreadyIn = activeSplit.exercises.some(
        e => e.exercise_id === exercise.id,
      );

      const nextExercises = alreadyIn
        ? activeSplit.exercises.filter(e => e.exercise_id !== exercise.id)
        : sortExercisesByGroup([
          ...activeSplit.exercises,
          {
            exercise_id: exercise.id,
            name: exercise.name,
            group: mainGroupName(exercise),
          },
        ]);

      updateActiveSplit({ exercises: nextExercises });
    },
    [activeSplit, updateActiveSplit],
  );

  const removeExerciseAt = useCallback(
    (exercise_id: string) => {
      updateActiveSplit({
        exercises: activeSplit.exercises.filter(
          e => e.exercise_id !== exercise_id,
        ),
      });
    },
    [activeSplit, updateActiveSplit],
  );

  const librarySearchResults = useMemo(() => {
    const search = librarySearch.trim().toLowerCase();

    return libraryExercises.filter(exercise =>
      exercise.name.toLowerCase().includes(search),
    );
  }, [libraryExercises, librarySearch]);

  const libraryMuscleGroupOptions = useMemo(() => {
    const groups = new Set(libraryExercises.map(mainGroupName));

    return Array.from(groups).sort((a, b) => a.localeCompare(b));
  }, [libraryExercises]);

  const libraryFilteredExercises = useMemo(() => {
    if (!libraryGroupFilter) return librarySearchResults;

    return librarySearchResults.filter(
      exercise => mainGroupName(exercise) === libraryGroupFilter,
    );
  }, [librarySearchResults, libraryGroupFilter]);

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      addToast({ type: 'error', title: 'Informe o nome do template.' });
      return;
    }

    if (!focus) {
      addToast({ type: 'error', title: 'Selecione o foco principal.' });
      return;
    }

    const payload = {
      name,
      focus,
      splits: splits.map(split => ({
        exercises: split.exercises.map(exercise => ({
          exercise_id: exercise.exercise_id,
        })),
      })),
    };

    setSaving(true);

    const request = isEditing
      ? trainingTemplatesService.update(id as string, payload)
      : trainingTemplatesService.create(payload);

    request
      .then(() => {
        addToast({
          type: 'success',
          title: isEditing ? 'Template atualizado' : 'Template criado',
        });
        // Não chama setSaving(false) aqui -- a navegação já desmonta o
        // componente, e o próprio React avisa (com razão) se um setState
        // rodar depois disso.
        navigate('/trainings/templates');
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Não foi possível salvar o template.',
        });
        setSaving(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, focus, splits, isEditing, id]);

  return (
    <Painel loading={loading}>
      <Container>
        <BackLink
          type="button"
          onClick={() => navigate('/trainings/templates')}
        >
          <Icon name="arrow_back" size={18} />
          Templates de treino
        </BackLink>

        <Header>
          <PageTitle>{isEditing ? 'Editar template' : 'Novo template'}</PageTitle>

          <HeaderActions>
            <Discard
              type="button"
              onClick={() => navigate('/trainings/templates')}
            >
              Descartar
            </Discard>
            <Save type="button" disabled={saving} onClick={handleSave}>
              {isEditing ? 'Salvar' : 'Criar template'}
            </Save>
          </HeaderActions>
        </Header>

        <Card>
          <SectionHead>
            <h3>Identificação</h3>
            <div className="ln" />
          </SectionHead>

          <FieldsRow>
            <Field>
              <FieldLabel>
                Nome do template <span className="req">*</span>
              </FieldLabel>
              <TextField
                value={name}
                onChange={event => setName(event.target.value)}
                placeholder="Ex.: Hipertrofia ABC"
              />
            </Field>

            <Field>
              <FieldLabel>
                Foco principal <span className="req">*</span>
              </FieldLabel>
              <TextField
                value={focus}
                onChange={event => setFocus(event.target.value)}
                placeholder="Ex.: Hipertrofia, Emagrecimento, Condicionamento..."
              />
            </Field>
          </FieldsRow>
        </Card>

        <Card>
          <SectionHead>
            <h3>Splits do template</h3>
            <div className="ln" />
          </SectionHead>

          <SplitTabsRow>
            {splits.map((split, index) => (
              <SplitTabGroup
                // eslint-disable-next-line react/no-array-index-key
                key={index}
              >
                <SplitTab
                  type="button"
                  active={index === activeSplitIndex}
                  onClick={() => setActiveSplitIndex(index)}
                  title={splitMuscleGroupsLabel(split)}
                >
                  <b>Split {SPLIT_LETTERS[index] || index + 1}</b>
                  <span>{split.exercises.length} ex.</span>
                  <small>{splitMuscleGroupsLabel(split)}</small>
                </SplitTab>

                {index === splits.length - 1 && splits.length > 1 && (
                  <DeleteSplitButton
                    type="button"
                    title="Apagar último split"
                    onClick={handleRemoveSplit}
                  >
                    <Icon name="delete" size={16} color="currentColor" />
                  </DeleteSplitButton>
                )}
              </SplitTabGroup>
            ))}

            <AddSplitTab type="button" onClick={handleAddSplit}>
              <Icon name="add" color={colors.primary} />
            </AddSplitTab>
          </SplitTabsRow>

          <EditorGrid>
            <SplitEditorColumn>
              <SplitHead>
                <div
                  style={{ gap: 8, display: 'flex', flexDirection: 'column' }}
                >
                  <SplitHeadTitle>
                    Split{' '}
                    {SPLIT_LETTERS[activeSplitIndex] || activeSplitIndex + 1}
                  </SplitHeadTitle>
                  <SplitHeadLabel>
                    {splitMuscleGroupsLabel(activeSplit)}
                  </SplitHeadLabel>
                </div>
              </SplitHead>

              <SmallLabel>Exercícios deste split</SmallLabel>

              <SplitExercisesScroll>
                {activeSplit.exercises.length ? (
                  <ExerciseList>
                    {activeSplit.exercises.map((exercise, index) => (
                      <ExerciseItem key={exercise.exercise_id}>
                        <ExerciseOrder>
                          {String(index + 1).padStart(2, '0')}
                        </ExerciseOrder>
                        <ExerciseInfo>
                          <p className="name">{exercise.name}</p>
                          <p className="group">{exercise.group}</p>
                        </ExerciseInfo>
                        <RemoveExerciseButton
                          type="button"
                          onClick={() => removeExerciseAt(exercise.exercise_id)}
                        >
                          <Icon name="close" size={20} />
                        </RemoveExerciseButton>
                      </ExerciseItem>
                    ))}
                  </ExerciseList>
                ) : (
                  <EmptySplit>
                    <Icon
                      name="fitness_center"
                      size={28}
                      color={colors.onSurfaceVariant}
                    />
                    <p>Nenhum exercício ainda.</p>
                    <p>Selecione na biblioteca ao lado.</p>
                  </EmptySplit>
                )}
              </SplitExercisesScroll>
            </SplitEditorColumn>

            <LibraryColumn>
              <SmallLabel>Biblioteca de exercícios</SmallLabel>

              <LibraryFiltersRow>
                <LibrarySearch>
                  <Icon
                    name="search"
                    size={18}
                    color={colors.onSurfaceVariant}
                  />
                  <input
                    placeholder="Buscar exercício..."
                    value={librarySearch}
                    onChange={event => setLibrarySearch(event.target.value)}
                  />
                </LibrarySearch>

                <LibraryGroupSelect
                  value={libraryGroupFilter}
                  onChange={event => setLibraryGroupFilter(event.target.value)}
                >
                  <option value="">Todos os grupos</option>
                  {libraryMuscleGroupOptions.map(group => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </LibraryGroupSelect>
              </LibraryFiltersRow>

              <LibraryScroll>
                <LibraryCardsGrid>
                  {libraryFilteredExercises.map(exercise => {
                    const active = activeSplit.exercises.some(
                      e => e.exercise_id === exercise.id,
                    );

                    return (
                      <LibraryCard
                        key={exercise.id}
                        active={active}
                        onClick={() => toggleExercise(exercise)}
                      >
                        <LibraryCardInfo>
                          <p className="name">{exercise.name}</p>
                          <span className="group">
                            {mainGroupName(exercise)}
                          </span>
                        </LibraryCardInfo>

                        <LibraryCardToggle active={active}>
                          <Icon
                            name={active ? 'check' : 'add'}
                            size={16}
                            color={active ? colors.onSuccess : colors.primary}
                          />
                        </LibraryCardToggle>
                      </LibraryCard>
                    );
                  })}
                </LibraryCardsGrid>
              </LibraryScroll>
            </LibraryColumn>
          </EditorGrid>
        </Card>
      </Container>
    </Painel>
  );
};

export { TrainingTemplateEditor };
